import { useDeferredValue, useState, type ReactNode } from 'react';
import { ShieldCheck, Trash2, TriangleAlert, UserPlus, Users } from 'lucide-react';
import { requiresMfa } from '@cp/shared';
import {
  Button,
  Card,
  EmptyState,
  LoadMore,
  PageHeader,
  SearchInput,
  StatusPill,
  TableSkeleton,
} from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { CreateUserDialog } from '@/components/users/CreateUserDialog';
import { DeleteUserDialog } from '@/components/users/DeleteUserDialog';
import { RolesDrawer } from '@/components/users/RolesDrawer';
import { UserAccessDialog } from '@/components/users/UserAccessDialog';
import { useCurrentUser } from '@/hooks/useAuth';
import { useUsers } from '@/hooks/useUsers';
import type { UserSummary } from '@/types/api';
import { formatRelative } from '@/utils/format';
import { roleList, USER_STATUS } from '@/utils/labels';

/**
 * The team: who has access to the panel and what each may do.
 */
export function UsersPage(): ReactNode {
  const currentUserId = useCurrentUser().data?.id;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [changing, setChanging] = useState<UserSummary | null>(null);
  const [removing, setRemoving] = useState<UserSummary | null>(null);
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim());

  // Page by page, searchable: a team outgrows one screen, and an account that
  // cannot be found on the page cannot be disabled from it either.
  const users = useUsers(query);
  const items = users.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="page">
      <PageHeader
        title="Сотрудники"
        subtitle="Учётные записи и роли"
        actions={
          <Button variant="primary" icon={UserPlus} onClick={() => setCreating(true)}>
            Добавить
          </Button>
        }
      />

      <div className="toolbar">
        <div className="toolbar-search">
          <SearchInput label="Поиск сотрудника" placeholder="Логин, имя или почта…" value={search} onChange={setSearch} />
        </div>
      </div>

      <Card flush>
        {users.isPending ? (
          <TableSkeleton rows={4} columns={4} />
        ) : users.isError ? (
          <ErrorState error={users.error} />
        ) : items.length === 0 ? (
          <EmptyState icon={Users} message={query ? 'Никого не нашлось' : 'Учётных записей нет'} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Сотрудник</th>
                  <th scope="col">Роли</th>
                  <th scope="col">Доступ</th>
                  <th scope="col">Последний вход</th>
                  <th scope="col" className="shrink" />
                </tr>
              </thead>
              <tbody>
                {items.map((user) => {
                  const self = user.id === currentUserId;

                  return (
                    <tr
                      key={user.id}
                      className="row-link"
                      tabIndex={0}
                      onClick={() => setEditing(user)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') setEditing(user);
                      }}
                    >
                      <td>
                        <div className="stack stack-tight">
                          <div className="row-tight">
                            <span className="strong">{user.displayName ?? user.login}</span>
                            {user.displayName ? <span className="mono faint">{user.login}</span> : null}
                            {self ? <span className="tag">это вы</span> : null}
                            {user.mfaEnabled ? (
                              <ShieldCheck size={13} aria-label="Вход с кодом подтверждения" className="muted" />
                            ) : requiresMfa(user.roles) ? (
                              // A role that needs a second factor and has none
                              // bound is the account that needs attention, so
                              // it is marked rather than left looking normal.
                              <span className="tag tone-degraded" title="Настроит при следующем входе">
                                <TriangleAlert size={11} aria-hidden="true" />
                                без второго фактора
                              </span>
                            ) : null}
                          </div>
                          <span className="muted">{user.email}</span>
                        </div>
                      </td>
                      <td>{roleList(user.roles)}</td>
                      <td>
                        <StatusPill map={USER_STATUS} status={user.status} />
                      </td>
                      <td className="muted nowrap" title={user.lastLoginAt ?? ''}>
                        {user.lastLoginAt ? formatRelative(user.lastLoginAt) : 'ни разу'}
                      </td>
                      <td className="shrink">
                        {/* Disabling your own account locks you out of the panel
                            that would let you undo it, so the server refuses;
                            the button is not offered either. */}
                        {self ? null : (
                          <div className="row-tight nowrap">
                            <Button
                              size="sm"
                              variant={user.status === 'active' ? 'danger' : 'default'}
                              onClick={(event) => {
                                event.stopPropagation();
                                setChanging(user);
                              }}
                            >
                              {user.status === 'active' ? 'Отключить' : 'Включить'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={Trash2}
                              aria-label={`Удалить ${user.login}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setRemoving(user);
                              }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LoadMore shown={items.length} hasMore={users.hasNextPage} busy={users.isFetchingNextPage} onMore={() => void users.fetchNextPage()} />

      <CreateUserDialog open={creating} onClose={() => setCreating(false)} />
      <DeleteUserDialog user={removing} onClose={() => setRemoving(null)} />
      <RolesDrawer user={editing} isSelf={editing?.id === currentUserId} onClose={() => setEditing(null)} />
      <UserAccessDialog user={changing} onClose={() => setChanging(null)} />
    </div>
  );
}
